/**
 * QL-375 — the basis table of screen 01: Portfolio · Type and schedule ·
 * Projected, end of year 1 · Share of basis, with the combined row. The
 * schedule column reads "Model · Balanced 5 · One-time · Mar 2027" or
 * "Custom · Recurring · 5 000 / month". The header names the anniversary
 * date on hover, as the charts' legend chips do.
 */

import { formatMandatePct } from "@repo/shared";
import type { GoalMandateEvaluation, MandateBasisPortfolio, Translated } from "@repo/shared/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatForecastGridDate } from "@/lib/date-utils";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./mandate-basis-table.i18n";

type Props = {
  basis: GoalMandateEvaluation["basis"];
  language: string;
};

const fmtAmount = (v: number, language: string): string =>
  v.toLocaleString(language === "nb_NO" ? "nb-NO" : "en-GB", { maximumFractionDigits: 0 });

function monthYear(date: string, language: string): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(language === "nb_NO" ? "nb-NO" : "en-GB", {
    month: "short",
    year: "numeric",
  });
}

function scheduleText(p: MandateBasisPortfolio, language: string, t: Translated<typeof T>) {
  const type =
    p.portfolioType === "model"
      ? t.typeModel
      : p.portfolioType === "dynamic"
        ? t.typeDynamic
        : t.typeCustom;
  const parts: string[] = [type];
  if (p.modelName) parts.push(p.modelName);
  if (p.scheduleType === "recurring") {
    parts.push(t.recurring);
    if (p.recurringAmount != null) {
      const per =
        p.recurringFrequency === "quarterly"
          ? t.perQuarter
          : p.recurringFrequency === "yearly"
            ? t.perYear
            : t.perMonth;
      parts.push(`${fmtAmount(p.recurringAmount, language)} ${per}`);
    }
  } else {
    parts.push(t.oneTime);
    if (p.oneTimeDate) parts.push(monthYear(p.oneTimeDate, language));
    // The buy basis: the row is the new money's part of the portfolio.
    if (p.basisKind === "buy") parts.push(t.basisBuy);
  }
  if (p.isFollowup) parts.push(t.followup);
  return parts.join(" · ");
}

export function MandateBasisTable({ basis, language }: Props) {
  const t = useT(T);
  const year1Hint = basis.anniversaryDate
    ? t.year1Hint(formatForecastGridDate(basis.anniversaryDate, language))
    : undefined;
  return (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead>{t.colPortfolio}</TableHead>
            <TableHead>{t.colTypeSchedule}</TableHead>
            <TableHead className="text-right" title={year1Hint}>
              {t.colProjected}
            </TableHead>
            <TableHead className="text-right">{t.colShare}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {basis.portfolios.map((p) => (
            <TableRow key={p.goalPortfolioId}>
              <TableCell className="font-medium">{p.name}</TableCell>
              <TableCell className="text-muted-foreground">
                {scheduleText(p, language, t)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {fmtAmount(p.projectedYear1, language)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatMandatePct(p.sharePct)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-muted/30 font-medium">
            <TableCell colSpan={2}>{t.combined}</TableCell>
            <TableCell className="text-right tabular-nums">
              {fmtAmount(basis.total, language)}
            </TableCell>
            <TableCell className="text-right tabular-nums">100%</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
